import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ShieldCheck, Search, Copy } from "lucide-react";
import { toast } from "sonner";
import { usePermissions } from "@/hooks/usePermissions";

const emptyForm = { code: "", label: "", description: "", policy_type: "", sort_order: 100, priority: "utile" };

const PRIORITY_LABELS = {
  essenziale: "Essenziale",
  utile: "Utile",
  opzionale: "Opzionale",
};
const PRIORITY_STYLES = {
  essenziale: "bg-rose-50 text-rose-700 border-rose-200",
  utile: "bg-blue-50 text-blue-700 border-blue-200",
  opzionale: "bg-slate-50 text-slate-600 border-slate-200",
};

export default function PolicyCoverages() {
  const { canWrite } = usePermissions();
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("__all__");

  const load = useCallback(async () => {
    try {
      const [{ data: covs }, { data: tps }] = await Promise.all([
        api.get("/policy-coverages"),
        api.get("/policy-types"),
      ]);
      setItems(covs);
      setTypes(tps);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const typeLabel = useCallback((code) => {
    if (!code) return "Tutte le tipologie";
    const t = types.find((x) => x.code === code);
    return t?.label || code;
  }, [types]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code || "",
      label: c.label || "",
      description: c.description || "",
      policy_type: c.policy_type || "",
      sort_order: c.sort_order ?? 100,
      priority: c.priority || "utile",
    });
    setOpen(true);
  };
  // 🆕 Duplica una garanzia di sistema come custom per l'agenzia corrente.
  const openDuplicate = (c) => {
    setEditing(null); // → POST
    setForm({
      code: `${c.code || ""}_copy`,
      label: `${c.label || ""} (Copia)`,
      description: c.description || "",
      policy_type: c.policy_type || "",
      sort_order: (c.sort_order ?? 100) + 1,
      priority: c.priority || "utile",
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 100 };
      if (editing) await api.put(`/policy-coverages/${editing.id}`, payload);
      else await api.post("/policy-coverages", payload);
      toast.success(editing ? "Garanzia aggiornata" : "Garanzia creata");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = (c) => setDeleteTarget(c);
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/policy-coverages/${deleteTarget.id}`);
      toast.success("Garanzia eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeleting(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      if (filterType !== "__all__") {
        const target = filterType === "__universal__" ? "" : filterType;
        if ((c.policy_type || "") !== target) return false;
      }
      if (!q) return true;
      return [c.code, c.label, c.description].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [items, search, filterType]);

  // Raggruppa per tipologia per la visualizzazione
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((c) => {
      const key = c.policy_type || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    // Ordina i gruppi: tipi noti per sort_order, poi "tutte" in fondo
    const orderedKeys = [...map.keys()].sort((a, b) => {
      if (a === "" && b !== "") return 1;
      if (b === "" && a !== "") return -1;
      const ta = types.find((t) => t.code === a)?.sort_order ?? 999;
      const tb = types.find((t) => t.code === b)?.sort_order ?? 999;
      return ta - tb;
    });
    return orderedKeys.map((k) => ({ type: k, list: map.get(k) }));
  }, [filtered, types]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="policy-coverages-page">
      <header className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Configurazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Garanzie Polizze</h1>
          <p className="text-slate-500 mt-1">{items.length} garanzie disponibili · seleziona quelle da proporre nei preventivi</p>
        </div>
        {canWrite("categories") && (
          <Button data-testid="add-coverage-btn" onClick={openCreate} className="bg-[#0F172A] hover:bg-[#1E293B]">
            <Plus className="h-4 w-4 mr-2" /> Nuova garanzia
          </Button>
        )}
      </header>

      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs">Cerca</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input data-testid="coverage-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Codice, etichetta o descrizione…" className="pl-8" />
          </div>
        </div>
        <div className="min-w-[200px]">
          <Label className="text-xs">Tipologia polizza</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger data-testid="coverage-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              <SelectItem value="__universal__">— Universali (tutte le polizze)</SelectItem>
              {types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 ml-auto">{filtered.length} di {items.length}</div>
      </div>

      <div className="space-y-5">
        {grouped.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400 text-sm">Nessuna garanzia trovata.</div>
        ) : grouped.map(({ type, list }) => (
          <section key={type || "__none__"} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <header className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-blue-600" />
              <span className="font-semibold text-sm">{typeLabel(type)}</span>
              <span className="text-xs text-slate-500">({list.length})</span>
            </header>
            <table className="w-full text-sm">
              <thead className="bg-white text-xs uppercase tracking-wide text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2 w-[180px]">Codice</th>
                  <th className="text-left px-4 py-2 w-[260px]">Etichetta</th>
                  <th className="text-left px-4 py-2 w-[110px]">Priorità</th>
                  <th className="text-left px-4 py-2">Descrizione</th>
                  <th className="text-right px-4 py-2 w-[60px]">Ord.</th>
                  <th className="text-right px-4 py-2 w-[100px]">Azioni</th>
                </tr>
              </thead>
              <tbody data-testid={`coverages-table-${type || "any"}`}>
                {list.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`coverage-row-${c.code}`}>
                    <td className="px-4 py-2.5 text-xs font-mono text-slate-700">
                      {c.code}
                      {(c.is_system || !c.agency_id) ? (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-600" title="Garanzia di sistema condivisa (non modificabile)">🔒 Sistema</span>
                      ) : (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700" title="Custom della tua agenzia">✏️ Custom</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{c.label}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded border ${PRIORITY_STYLES[c.priority] || PRIORITY_STYLES.utile}`}>
                        {PRIORITY_LABELS[c.priority] || PRIORITY_LABELS.utile}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{c.description || "—"}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-500 tabular-nums">{c.sort_order}</td>
                    <td className="px-4 py-2.5 text-right space-x-2">
                      {canWrite("categories") && (
                        <>
                          {(c.is_system || !c.agency_id) ? (
                            <>
                              <span className="text-slate-300 cursor-not-allowed" title="Garanzia di sistema — non modificabile. Creane una custom.">
                                <Pencil className="h-3.5 w-3.5 inline" />
                              </span>
                              <button
                                data-testid={`duplicate-coverage-${c.code}`}
                                onClick={() => openDuplicate(c)}
                                className="text-emerald-600 hover:text-emerald-800"
                                title="Duplica come custom per la tua agenzia"
                              >
                                <Copy className="h-3.5 w-3.5 inline" />
                              </button>
                            </>
                          ) : (
                            <button data-testid={`edit-coverage-${c.code}`} onClick={() => openEdit(c)} className="text-blue-600 hover:text-blue-800" title="Modifica">
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {!(c.is_system || !c.agency_id) && (
                            <button data-testid={`delete-coverage-${c.code}`} onClick={() => remove(c)} className="text-red-600 hover:text-red-800" title="Elimina">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ))}
      </div>

      {/* Dialog create/edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="coverage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              {editing ? "Modifica garanzia" : "Nuova garanzia"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Codice tecnico *</Label>
              <Input
                data-testid="coverage-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                disabled={editing?.is_system}
                placeholder="es. kasko, furto_incendio"
                className="font-mono lowercase"
              />
              <p className="text-[10px] text-slate-500 mt-1">Solo lettere minuscole, numeri e _</p>
            </div>
            <div>
              <Label>Tipologia polizza</Label>
              <Select value={form.policy_type || "__none__"} onValueChange={(v) => setForm({ ...form, policy_type: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="coverage-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Universale (tutte) —</SelectItem>
                  {types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1">A quale tipo di polizza appartiene</p>
            </div>
            <div className="col-span-2">
              <Label>Etichetta visibile *</Label>
              <Input
                data-testid="coverage-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Es. Kasko (collisione)"
              />
            </div>
            <div className="col-span-2">
              <Label>Descrizione</Label>
              <Textarea
                data-testid="coverage-description"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Breve descrizione per l'operatore o il cliente"
              />
            </div>
            <div>
              <Label>Priorità</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="coverage-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="essenziale">Essenziale (obbligatoria/raccomandata)</SelectItem>
                  <SelectItem value="utile">Utile (consigliata)</SelectItem>
                  <SelectItem value="opzionale">Opzionale</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1">Aiuta operatore/cliente a capire l&apos;importanza</p>
            </div>
            <div>
              <Label>Ordine</Label>
              <Input
                type="number"
                data-testid="coverage-order"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                placeholder="100"
              />
              <p className="text-[10px] text-slate-500 mt-1">Numero più basso = appare prima</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-coverage" disabled={!form.code.trim() || !form.label.trim()} onClick={submit} className="bg-[#0F172A] hover:bg-[#1E293B]">
              {editing ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-coverage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare la garanzia?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>Stai per eliminare:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2">{deleteTarget?.label}</p>
            <p className="text-xs text-red-600">L&apos;operazione non può essere annullata. I preventivi già salvati manterranno il riferimento ma la garanzia non sarà più selezionabile.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-delete-coverage" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annulla</Button>
            <Button data-testid="confirm-delete-coverage" onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? "Eliminazione…" : "Elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
